import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { usePermission } from '@/lib/permissions'
import { sameSource, trailCrumb, trailTitle, type TrailSource } from '@/lib/auditTrailSources'
import { useAuditTrailSource } from '@/hooks/useAuditTrailSource'
import { AuditTrailTable } from './AuditTrailTable'
import { AuditTrailDialogContext } from './auditTrailDialogContext'

/**
 * The audit pop-up (z8nrz7dq6q): a hyperlink in any audit trail opens the linked
 * item's OWN trail here instead of taking the reader to the item's page.
 *
 * Links inside the pop-up open the next trail in the same dialog, with a back
 * trail of where the reader came from — never a stack of dialogs. "Open item"
 * keeps the old route to the item's page, and browser Back closes the pop-up
 * rather than leaving the record underneath.
 *
 * Mounted once, in the app layout, so every audit table on every screen gets it.
 */
export function AuditTrailDialogProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<TrailSource[]>([])
  const current = stack.length > 0 ? stack[stack.length - 1] : null

  const open = useCallback(
    (source: TrailSource) =>
      setStack(prev => (sameSource(prev[prev.length - 1], source) ? prev : [...prev, source])),
    [],
  )
  const value = useMemo(() => ({ open, current }), [open, current])

  return (
    <AuditTrailDialogContext.Provider value={value}>
      {children}
      <AuditTrailDialog stack={stack} setStack={setStack} />
    </AuditTrailDialogContext.Provider>
  )
}

function AuditTrailDialog({
  stack,
  setStack,
}: {
  stack: TrailSource[]
  setStack: (next: TrailSource[] | ((prev: TrailSource[]) => TrailSource[])) => void
}) {
  const navigate = useNavigate()
  const { canAny } = usePermission()
  const isOpen = stack.length > 0
  const current = isOpen ? stack[stack.length - 1] : null
  const trail = useAuditTrailSource(current)
  // Set when leaving through "Open item", whose navigation REPLACES the history
  // entry this dialog pushed — stepping back on close would then undo it.
  const leavingForItem = useRef(false)

  // Browser Back closes the pop-up instead of leaving the record behind it.
  useEffect(() => {
    if (!isOpen) return
    window.history.pushState({ auditTrailPopup: true }, '')
    const onPop = () => setStack([])
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      if (leavingForItem.current) {
        leavingForItem.current = false
        return
      }
      if (window.history.state?.auditTrailPopup) window.history.back()
    }
  }, [isOpen, setStack])

  const openItem = current?.openItem && canAny(current.openItem.permissions) ? current.openItem : null

  return (
    <Dialog open={isOpen} onOpenChange={o => !o && setStack([])}>
      {/* Wide for the same reason as the product trail: eleven columns. */}
      <DialogContent className='flex max-h-[90vh] flex-col gap-3 sm:max-w-[95vw]'>
        <DialogHeader className='space-y-1.5 pr-8'>
          {stack.length > 1 && (
            <nav aria-label='Audit trails opened' className='flex flex-wrap items-center gap-1 text-xs'>
              {stack.map((s, i) => {
                const isLast = i === stack.length - 1
                return (
                  <span key={`${s.kind}:${s.id}:${i}`} className='flex items-center gap-1'>
                    {isLast ? (
                      <span className='font-medium text-foreground'>{trailCrumb(s)}</span>
                    ) : (
                      <button
                        type='button'
                        className='text-muted-foreground hover:text-foreground hover:underline'
                        onClick={() => setStack(prev => prev.slice(0, i + 1))}
                      >
                        {trailCrumb(s)}
                      </button>
                    )}
                    {!isLast && <ChevronRight className='h-3 w-3 text-muted-foreground/60' />}
                  </span>
                )
              })}
            </nav>
          )}
          <div className='flex flex-wrap items-center gap-2'>
            {stack.length > 1 && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-7 px-2'
                aria-label='Back to the previous audit trail'
                onClick={() => setStack(prev => prev.slice(0, -1))}
              >
                <ArrowLeft className='h-4 w-4' />
              </Button>
            )}
            <DialogTitle className='text-base'>{current ? trailTitle(current) : 'Audit trail'}</DialogTitle>
            {openItem && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='ml-auto h-7 gap-1.5'
                onClick={() => {
                  leavingForItem.current = true
                  setStack([])
                  navigate(openItem.to, { replace: true })
                }}
              >
                <ExternalLink className='h-3.5 w-3.5' />
                Open item
              </Button>
            )}
          </div>
          <DialogDescription className='text-xs'>
            Every recorded change, newest first. Links open that item&rsquo;s own trail here.
            {trail.data?.total !== undefined &&
              ` Showing the latest ${trail.data.rows.length} of ${trail.data.total}.`}
          </DialogDescription>
        </DialogHeader>

        {trail.isError ? (
          <p role='alert' className='rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
            This audit trail could not be loaded. {(trail.error as Error)?.message}
          </p>
        ) : (
          <AuditTrailTable
            tableId='audit-trail-popup'
            rows={trail.data?.rows ?? []}
            isLoading={trail.isLoading}
            emptyMessage='Nothing recorded for this item yet.'
            maxHeightClass='max-h-[65vh]'
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
